import styled from '@emotion/styled'
import { IEvent } from '../../event'
import { Checkbox, ReadOnlyText } from '../../generic-components'

import { ISummaryLabel, SummaryLabel } from '../../generic-components/SummaryLabel/SummaryLabels'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { TFunctionalityAction } from '../../view-engine'
import { translate } from '../../localization'
import { initEventAction } from '../State/EmployeeCalendarThunks'
import { selectSelectedEvent } from '../State/EmployeeCalendarSelectors'
import { IEmployeeDataSourceItem } from '../../messaging/Types/IEmployeeDataSourceItem'
import { RootState } from 'typesafe-actions'
import { toggleSelectedEvent } from '../State/EmployeeCalendarReducer'
import { CancelEventAction, DeleteEventAction, EditEventAction, getPermission } from '../Utils/eventActions'
import ActionButton from './ActionButton'
import EventCategoryIndicator from './EventCategoryIndicator'

type Props = {
    event: IEvent
    employee?: IEmployeeDataSourceItem
    drawBottomBorder?: boolean
}

const Container = styled.div<{ bottomBorder?: boolean }>`
    padding: 8px 24px 12px 20px;
    width: 100%;
    ${({ bottomBorder, theme }) =>
        bottomBorder && `border-bottom: 1px solid ${theme.componentExtensions.border.secondary};`}
`

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`

const Row = styled.div<{ height?: number }>`
    height: ${({ height }) => height && `${height}px`};
    display: flex;
    flex-direction: row;
    margin-left: 48px;
`

const SummaryLabels = styled.div`
    display: flex;
    flex: 1;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
`

const StyledSummaryLabel = styled(SummaryLabel)`
    flex: 1 1 100%;
    justify-content: flex-start;
    align-items: flex-start;
`

const SummaryLabelPlaceholder = styled.div`
    flex: 1 1 100%;
`

export const IndicatorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 28px;
    height: 30px;
    margin-left: 26px;
    margin-right: 20px;
`

export const EventTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`

const ActionButtons = ({ event, employee }: Pick<Props, 'event' | 'employee'>): JSX.Element => {
    const dispatch = useDispatch()

    const initAction = async (action: TFunctionalityAction) => {
        dispatch(initEventAction(action, [event], employee))
    }

    const canBeCanceled = getPermission(event, CancelEventAction)?.OnkoSallittu

    const removeAction = canBeCanceled
        ? {
              icon: 'cancel',
              tooltip: 'Peru vuoro',
              permission: getPermission(event, CancelEventAction),
              onClick: () => initAction(CancelEventAction),
          }
        : {
              icon: 'delete',
              tooltip: 'remove-button',
              permission: getPermission(event, DeleteEventAction),
              onClick: () => initAction(DeleteEventAction),
          }

    const actions = [
        {
            icon: 'edit',
            tooltip: 'muokkaa',
            permission: getPermission(event, EditEventAction),
            onClick: () => initAction(EditEventAction),
        },
        removeAction,
    ]

    return (
        <ButtonContainer>
            {actions.map(({ icon, tooltip, permission, onClick }) => (
                <ActionButton
                    key={icon}
                    icon={icon}
                    tooltip={permission?.SyyKieltoon || translate(tooltip)}
                    onClick={onClick}
                    disabled={!permission?.OnkoSallittu}
                    testId="event-action"
                />
            ))}
        </ButtonContainer>
    )
}

const EventCard = ({ event, employee, drawBottomBorder = false }: Props): JSX.Element => {
    const dispatch = useDispatch()

    const isSelected = useSelector((state: RootState) => selectSelectedEvent(state, event.Id))

    const handleCheckboxClick = () => {
        dispatch(toggleSelectedEvent(event))
    }

    const eventTimePeriod = `${moment(event.StartTime).format('HH:mm')}–${moment(event.EndTime).format('HH:mm')}`
    const summaryLabels = [
        {
            Icon: 'access_time',
            Text: eventTimePeriod,
        },
        {
            Icon: 'business',
            Text: event.WorkUnit ? `${event.WorkUnit?.Id} ${event.WorkUnit?.Name}` : null,
        },
        {
            Icon: 'account_circle_outlined',
            Text: event.JobTitle?.Name,
        },
    ] as ISummaryLabel[]

    const labelsBlock = summaryLabels.map((summaryLabel, index) => {
        if (!summaryLabel.Text) {
            return <SummaryLabelPlaceholder key={`placeholder-${index}`} />
        }

        return <StyledSummaryLabel key={summaryLabel.Text} fontUsage="bodyS" summaryLabel={summaryLabel} noPadding />
    })

    return (
        <Container bottomBorder={drawBottomBorder} data-testid="calendar-event-card">
            <HeaderRow>
                <EventTextContainer>
                    <Checkbox onClick={handleCheckboxClick} value={Boolean(isSelected)} variant="large" noPadding />

                    <IndicatorContainer>
                        <EventCategoryIndicator eventTypeCategory={event.EventTypeCategory} />
                    </IndicatorContainer>

                    <ReadOnlyText usage="bodyS">{event.EventType.Name}</ReadOnlyText>
                </EventTextContainer>

                <ActionButtons event={event} employee={employee} />
            </HeaderRow>

            {event.EventGroup && <Row height={20}>{translate('reserving_event_group')}</Row>}

            <Row>
                <SummaryLabels>{labelsBlock}</SummaryLabels>
            </Row>
        </Container>
    )
}

export default EventCard
